import type { RampConfig } from '@gorampe/calc-engine';
import { optimizeTreadSections } from '@gorampe/calc-engine';
import type {
  SceneLayout,
  PlatformPlacement,
  TreadPlacement,
  RailPlacement,
  LandingPlacement,
  PattePlacement,
} from '../types';
import { SCALE_IN_TO_UNIT } from '../types';
import type { HardwareGroup } from '../hardware/instances';
import {
  DEFAULT_HEAD_PLATFORM_DEPTH_IN,
  DEFAULT_HEAD_PLATFORM_WIDTH_IN,
  DEFAULT_TURN_LANDING_SIZE_IN,
  EDGE_OVERLAP_IN,
} from './constants';
import { selectPatteSku, selectTransitionSku, selectCornerPostSku } from '../bom/skuMapping';

type L90Shape = Extract<RampConfig['shape'], { kind: 'L90' }>;

export function layoutL90(config: RampConfig, shape: L90Shape): SceneLayout {
  const platforms: PlatformPlacement[] = [];
  const treads: TreadPlacement[] = [];
  const rails: RailPlacement[] = [];
  const landings: LandingPlacement[] = [];
  const pattes: PattePlacement[] = [];
  const PATTE_INTERVAL_IN = 60; // patte every 5 ft

  // PDF officiel: head platform 60×60 (default) or 60×96. Turn landing 60 or 96.
  const headDepthIn = config.headPlatformDepthIn ?? DEFAULT_HEAD_PLATFORM_DEPTH_IN;
  const headWidthIn = DEFAULT_HEAD_PLATFORM_WIDTH_IN;
  const TURN_LANDING_SIZE_IN = shape.landingDepthIn ?? DEFAULT_TURN_LANDING_SIZE_IN;

  const totalRun = shape.arm1RunIn + shape.arm2RunIn;
  const arm1Height = config.heightIn * (shape.arm1RunIn / totalRun);
  const arm2Height = config.heightIn - arm1Height;
  const turnYIn = arm2Height;
  const pitchAngle = Math.atan2(arm1Height, shape.arm1RunIn);
  const turnSign = shape.turnDirection === 'left' ? 1 : -1;
  const arm2Rot = turnSign * (Math.PI / 2);

  // Head platform at top of arm1
  // PDF officiel: 60×60 (5×5) or 60×96 (5×8), independent of arm width.
  platforms.push({
    id: 'head',
    kind: 'head',
    position: {
      x: 0,
      y: config.heightIn * SCALE_IN_TO_UNIT,
      z: (-headDepthIn / 2 + EDGE_OVERLAP_IN) * SCALE_IN_TO_UNIT,
    },
    widthIn: headWidthIn,
    depthIn: headDepthIn,
  });

  // Turn landing at junction
  const turnCenterZ = shape.arm1RunIn + TURN_LANDING_SIZE_IN / 2;
  platforms.push({
    id: 'turn',
    kind: 'turn-landing',
    position: {
      x: 0,
      y: turnYIn * SCALE_IN_TO_UNIT,
      z: turnCenterZ * SCALE_IN_TO_UNIT,
    },
    widthIn: TURN_LANDING_SIZE_IN,
    depthIn: TURN_LANDING_SIZE_IN,
  });

  // Arm 1 treads (rotationY = 0, descending along +Z)
  let cursor1 = 0;
  for (const sec of optimizeTreadSections(shape.arm1RunIn)) {
    for (let i = 0; i < sec.qty; i++) {
      const midRun = cursor1 + sec.lengthIn / 2;
      const yIn = config.heightIn - (midRun / shape.arm1RunIn) * arm1Height;
      treads.push({
        id: `tread-a1-${sec.lengthIn}-${i}-${cursor1}`,
        position: { x: 0, y: yIn * SCALE_IN_TO_UNIT, z: midRun * SCALE_IN_TO_UNIT },
        rotationY: 0,
        pitchAngle,
        lengthIn: sec.lengthIn,
        widthIn: config.widthIn,
      });
      cursor1 += sec.lengthIn;
    }
  }

  // Arm 2: pivots around turn landing center, descends along ±X
  // ADJ-21: Root-cause fix for straddle bug. Previously arm2OriginZ = turnCenterZ + halfLanding
  // placed arm 2's centerline EXACTLY on the north edge of the landing. INSIDE rail
  // (centerline - xOff) sat 18" INSIDE the landing footprint (z=186 for palier 60),
  // OUTSIDE rail (centerline + xOff) floated 18" beyond the landing (z=222).
  // Fix: shift arm2 origin by +xOff so arm 2's south face aligns with the landing north edge.
  //   New arm2OriginZ = turnCenterZ + halfLanding + xOff
  //   For palier 60, widthIn=36: 174 + 30 + 18 = 222 (was 204)
  //   INSIDE rail now at z = arm2OriginZ - xOff = 204 (= landing north edge, no Y jump)
  //   OUTSIDE rail now at z = arm2OriginZ + xOff = 240 (= landing edge + widthIn)
  const arm2OriginX = 0;
  const arm2OriginZ = turnCenterZ + TURN_LANDING_SIZE_IN / 2 + config.widthIn / 2;
  const arm2Pitch = Math.atan2(arm2Height, shape.arm2RunIn);
  let cursor2 = 0;
  for (const sec of optimizeTreadSections(shape.arm2RunIn)) {
    for (let i = 0; i < sec.qty; i++) {
      const midRun = cursor2 + sec.lengthIn / 2;
      const yIn = turnYIn - (midRun / shape.arm2RunIn) * arm2Height;
      const dx = turnSign * midRun;
      treads.push({
        id: `tread-a2-${sec.lengthIn}-${i}-${cursor2}`,
        position: {
          x: (arm2OriginX + dx) * SCALE_IN_TO_UNIT,
          y: yIn * SCALE_IN_TO_UNIT,
          z: arm2OriginZ * SCALE_IN_TO_UNIT,
        },
        rotationY: arm2Rot,
        pitchAngle: arm2Pitch,
        lengthIn: sec.lengthIn,
        widthIn: config.widthIn,
      });
      cursor2 += sec.lengthIn;
    }
  }

  // Rails: 2 per arm — pitched + at mid-slope height
  if (config.railType !== 'aucun') {
    const railHeightIn = config.usage === 'com' ? 42 : 36;
    const xOff = (config.widthIn / 2) * SCALE_IN_TO_UNIT;
    const arm1MidY = config.heightIn - arm1Height / 2;
    const arm2MidY = turnYIn - arm2Height / 2;

    // Head platform side rails ONLY (left + right). NO back rail — building/doorway side
    const headTopY = config.heightIn * SCALE_IN_TO_UNIT;
    const headMidZ = (-headDepthIn / 2 + EDGE_OVERLAP_IN) * SCALE_IN_TO_UNIT;
    rails.push({
      id: 'rail-head-left',
      position: { x: -xOff, y: headTopY, z: headMidZ },
      rotationY: 0,
      pitchAngle: 0,
      lengthIn: headDepthIn,
      heightIn: railHeightIn,
      type: config.railType,
      side: 'left',
    });
    rails.push({
      id: 'rail-head-right',
      position: { x: xOff, y: headTopY, z: headMidZ },
      rotationY: 0,
      pitchAngle: 0,
      lengthIn: headDepthIn,
      heightIn: railHeightIn,
      type: config.railType,
      side: 'right',
    });

    const halfLanding = TURN_LANDING_SIZE_IN / 2;
    const xOffIn = config.widthIn / 2; // inches, e.g. 18" for 36"-wide ramp

    // ADJ-20: Refonte propre.
    // Arm rails (a1 + a2) ne sont PLUS étendus dans le palier. Ils restent à leur
    // longueur naturelle, pitchés, et leurs bouts uphill atterrissent à y=turnYIn
    // (= bord du palier). Les bridges plats (à y=turnYIn, pitchAngle=0) relient les
    // bouts de rails de bras au périmètre du palier pour assurer la continuité Y.
    for (const side of ['left', 'right'] as const) {
      const sideSign = side === 'left' ? 1 : -1;
      const isInside = sideSign * turnSign < 0;

      // ADJ-20: Arm 1 rail — longueur naturelle, NO extension into landing.
      // Bouts: uphill (z=0) à y=heightIn ; downhill (z=arm1RunIn) à y=turnYIn.
      // Le bout downhill atterrit pile sur le palier (y=turnYIn).
      const arm1RailLength = shape.arm1RunIn;
      const arm1RailCenterZ = arm1RailLength / 2;

      rails.push({
        id: `rail-a1-${side}`,
        position: {
          x: side === 'left' ? -xOff : xOff,
          y: arm1MidY * SCALE_IN_TO_UNIT,
          z: arm1RailCenterZ * SCALE_IN_TO_UNIT,
        },
        rotationY: 0,
        pitchAngle,
        lengthIn: arm1RailLength,
        heightIn: railHeightIn,
        type: config.railType,
        side,
      });

      // Arm 2 runs along world X (after rotationY=±π/2).
      // Perpendicular = world Z. Left/right rails offset in Z by ±xOff.
      // ADJ-20: INSIDE rail couvre x∈[xOff, arm2RunIn], donc son centre Y doit être
      //   recalculé depuis les vrais endpoints (uphill: turnYIn × (1 - xOff/arm2RunIn),
      //   downhill: 0). Le bug précédent utilisait arm2MidY (centre du range complet
      //   x∈[0, arm2RunIn]) ce qui décalait le rail INSIDE de ~1" vers le haut.
      // OUTSIDE rail couvre x∈[0, arm2RunIn] → centre Y = arm2MidY (inchangé).
      // Plus d'extension par halfLanding pour OUTSIDE.
      const arm1RightInsideCornerX = turnSign * xOffIn; // ±xOff
      let arm2RailLength: number;
      let a2MidX: number;
      let a2RailCenterY: number;
      if (isInside) {
        arm2RailLength = shape.arm2RunIn - xOffIn;
        a2MidX = (arm1RightInsideCornerX + turnSign * shape.arm2RunIn) / 2;
        // Recalc center.y from real endpoints (P0 fix #2)
        const yAtInsideStart = turnYIn * (1 - xOffIn / shape.arm2RunIn);
        a2RailCenterY = (yAtInsideStart + 0) / 2;
      } else {
        arm2RailLength = shape.arm2RunIn;
        a2MidX = (turnSign * shape.arm2RunIn) / 2;
        a2RailCenterY = arm2MidY;
      }
      // INSIDE z = -xOff (arm 2's south edge in arm-relative coords).
      // OUTSIDE z = sideSign * turnSign * xOff = +xOff (arm 2's north edge for left turn).
      const a2ZOffsetIn = isInside ? -xOffIn : sideSign * turnSign * xOffIn;
      rails.push({
        id: `rail-a2-${side}`,
        position: {
          x: a2MidX * SCALE_IN_TO_UNIT,
          y: a2RailCenterY * SCALE_IN_TO_UNIT,
          z: (arm2OriginZ + a2ZOffsetIn) * SCALE_IN_TO_UNIT,
        },
        rotationY: arm2Rot,
        pitchAngle: arm2Pitch,
        lengthIn: arm2RailLength,
        heightIn: railHeightIn,
        type: config.railType,
        side,
      });
    }

    // ADJ-21: SE inside bridge — closes the gap on the INSIDE (concave) corner.
    // Post-fix: arm 1 INSIDE ends at z=arm1RunIn (= turnCenterZ - halfLanding);
    //          arm 2 INSIDE now starts at z = arm2OriginZ - xOff = turnCenterZ + halfLanding (= landingFarZ).
    // Bridge spans the FULL landing depth in Z: length = TURN_LANDING_SIZE_IN (= 60" for default palier).
    // Was 42" pre-ADJ-21 (= TURN_LANDING - xOff) because arm 2 INSIDE landed 18" inside the palier.
    const insideBridgeLengthIn = TURN_LANDING_SIZE_IN; // full landing depth
    const insideBridgeCenterZIn = shape.arm1RunIn + insideBridgeLengthIn / 2;
    const insideCornerXIn = turnSign * xOffIn; // +18 for left, -18 for right
    const insideSide = turnSign > 0 ? 'right' : 'left';
    rails.push({
      id: 'rail-corner-se-inside-bridge',
      position: {
        x: insideCornerXIn * SCALE_IN_TO_UNIT,
        y: turnYIn * SCALE_IN_TO_UNIT,
        z: insideBridgeCenterZIn * SCALE_IN_TO_UNIT,
      },
      rotationY: 0,
      pitchAngle: 0,
      lengthIn: insideBridgeLengthIn,
      heightIn: railHeightIn,
      type: config.railType,
      side: insideSide,
    });

    // Turn-landing perimeter garde-corps — open sides (no arm attached)
    // South (-Z) = arm1 attaches; arm2 extends from far +Z side in ±X direction.
    // Open sides: NORTH (+Z), and the X-side OPPOSITE the turn direction.
    // RailTopTube extrudes along local Z. rotationY=π/2 → extends along world X.
    // (halfLanding already defined above for arm rail extension)
    // ADJ-20: Truncated landing-north rail (P0 fix #3) — must NOT cross arm 2's emergence.
    // Arm 2 emerges from the landing at x∈[0, +xOff] (for left turn) / x∈[-xOff, 0] (right turn).
    // The north rail only covers the half OPPOSITE to arm 2 direction:
    //   LEFT turn  (turnSign=+1): rail covers x∈[-halfLanding, 0], length=halfLanding=24, center.x=-12
    //   RIGHT turn (turnSign=-1): rail covers x∈[0, +halfLanding], length=halfLanding=24, center.x=+12
    const northRailLengthIn = halfLanding;
    const northRailCenterXIn = -turnSign * (halfLanding / 2);
    rails.push({
      id: 'rail-landing-north',
      position: {
        x: northRailCenterXIn * SCALE_IN_TO_UNIT,
        y: turnYIn * SCALE_IN_TO_UNIT,
        z: (turnCenterZ + halfLanding) * SCALE_IN_TO_UNIT,
      },
      rotationY: Math.PI / 2,
      pitchAngle: 0,
      lengthIn: northRailLengthIn,
      heightIn: railHeightIn,
      type: config.railType,
      side: 'right',
    });

    // AS.003: Outside (convex) corner wrap — continuous garde-corps along landing west edge.
    // For LEFT turn: west edge = x=-halfLanding=-24 (opposite to turn direction).
    // For RIGHT turn: east edge = x=+halfLanding=+24.
    // The landing west/east edge rail must sit at the PHYSICAL edge of the 48" landing,
    // NOT at xOff (arm width edge = 18"). This closes the visual gap on the outside corner.
    const oppositeSign = -turnSign;
    const halfLandingIn = halfLanding; // halfLanding already in inches
    const halfLandingSU = halfLandingIn * SCALE_IN_TO_UNIT;

    // rail-landing-side: full landing depth (48") along the outside edge (x = ±halfLanding)
    rails.push({
      id: 'rail-landing-side',
      position: {
        x: oppositeSign * halfLandingSU,
        y: turnYIn * SCALE_IN_TO_UNIT,
        z: turnCenterZ * SCALE_IN_TO_UNIT,
      },
      rotationY: 0,
      pitchAngle: 0,
      lengthIn: TURN_LANDING_SIZE_IN,
      heightIn: railHeightIn,
      type: config.railType,
      side: 'left',
    });

    // ADJ-21: OUTSIDE corner wrap — flat bridges replace the obsolete extended-rail approach.
    // Path for LEFT turn (mirror for RIGHT), palier 60×60, widthIn=36:
    //   arm1-OUTSIDE (left, ends at x=-xOff=-18, z=arm1RunIn=144, y=turnYIn)
    //   → SW bridge: x∈[-halfLanding=-30, -xOff=-18] at z=arm1RunIn, length=halfLanding-xOff=12"
    //   → landing-side (west edge at x=-halfLanding=-30, z∈[144, 204], length=60)
    //   → landing-north WEST half (x∈[-halfLanding=-30, 0] at z=arm1RunIn+60=204, length=30)
    //   → NW-north bridge: x=0, z∈[204, 240], length=widthIn=36 (joins arm2 outside rail south end)
    //   → arm2-OUTSIDE (left, starts at x=0, z=arm2OriginZ+xOff=240, y=turnYIn)
    // All bridges flat (pitchAngle=0) at y=turnYIn for clean Y continuity.

    // SW bridge: short perpendicular bridge from arm1-outside rail end to landing west edge.
    // Sits at z=arm1RunIn (south landing edge), spans x=[-halfLanding, -xOff] for left turn.
    const swBridgeLengthIn = halfLandingIn - xOffIn; // e.g. 30 - 18 = 12"
    const swBridgeCenterXIn = -turnSign * ((xOffIn + halfLandingIn) / 2);
    rails.push({
      id: 'rail-corner-sw-bridge',
      position: {
        x: swBridgeCenterXIn * SCALE_IN_TO_UNIT,
        y: turnYIn * SCALE_IN_TO_UNIT,
        z: shape.arm1RunIn * SCALE_IN_TO_UNIT,
      },
      rotationY: Math.PI / 2, // extends along world X
      pitchAngle: 0,
      lengthIn: swBridgeLengthIn,
      heightIn: railHeightIn,
      type: config.railType,
      side: 'left',
    });

    // ADJ-21: NW-north bridge — bridges the gap between landing north edge and arm 2 OUTSIDE rail.
    // Post-fix: landing north edge at z = turnCenterZ + halfLanding (= 204 for palier 60);
    //          arm 2 OUTSIDE rail now at z = arm2OriginZ + xOff = 240 (was 222 pre-ADJ-21).
    //          Gap to bridge = widthIn = 2 × xOff (= 36" for widthIn=36).
    // Sits at x=0 (arm 2's inside-X edge at origin for left turn).
    const nwNorthBridgeLengthIn = config.widthIn; // = 36" for std 36-wide ramp
    const nwNorthBridgeCenterZIn = turnCenterZ + halfLanding + nwNorthBridgeLengthIn / 2;
    rails.push({
      id: 'rail-corner-nw-north-bridge',
      position: {
        x: 0,
        y: turnYIn * SCALE_IN_TO_UNIT,
        z: nwNorthBridgeCenterZIn * SCALE_IN_TO_UNIT,
      },
      rotationY: 0, // extends along world Z
      pitchAngle: 0,
      lengthIn: nwNorthBridgeLengthIn,
      heightIn: railHeightIn,
      type: config.railType,
      side: 'left',
    });
  }

  // Pattes — arm 1: x=0, running along +Z
  const arm1PatteCount = Math.max(1, Math.floor(shape.arm1RunIn / PATTE_INTERVAL_IN));
  for (let i = 1; i <= arm1PatteCount; i++) {
    const fraction = i / (arm1PatteCount + 1);
    const zIn = shape.arm1RunIn * fraction;
    const patteYIn = config.heightIn - fraction * arm1Height;
    if (patteYIn < 1) continue;
    pattes.push({
      id: `patte-a1-${i}`,
      position: {
        x: 0,
        y: patteYIn * SCALE_IN_TO_UNIT,
        z: zIn * SCALE_IN_TO_UNIT,
      },
      rotationY: 0,
      widthIn: config.widthIn,
      heightIn: patteYIn,
      partNumber: selectPatteSku(patteYIn),
    });
  }

  // Pattes — arm 2: x varies along ±X (turnSign * dist), z = arm2OriginZ (constant)
  const arm2PatteCount = Math.max(1, Math.floor(shape.arm2RunIn / PATTE_INTERVAL_IN));
  for (let i = 1; i <= arm2PatteCount; i++) {
    const fraction = i / (arm2PatteCount + 1);
    const distIn = shape.arm2RunIn * fraction;
    const xIn = turnSign * distIn;
    const patteYIn = turnYIn - fraction * arm2Height;
    if (patteYIn < 1) continue;
    pattes.push({
      id: `patte-a2-${i}`,
      position: {
        x: xIn * SCALE_IN_TO_UNIT,
        y: patteYIn * SCALE_IN_TO_UNIT,
        z: arm2OriginZ * SCALE_IN_TO_UNIT,
      },
      rotationY: arm2Rot,
      widthIn: config.widthIn,
      heightIn: patteYIn,
      partNumber: selectPatteSku(patteYIn),
    });
  }

  const hardwareGroups: HardwareGroup[] = [];

  // Corner posts + transition for L90
  const cornerPosts = config.railType !== 'aucun' ? buildL90CornerPosts(config, shape) : [];
  const transitions = buildL90Transition(config, shape);

  return {
    platforms,
    treads,
    rails,
    landings,
    pattes,
    cornerPosts,
    transitions,
    hardwareGroups,
    floor: config.floor,
  };
}

function buildL90CornerPosts(config: RampConfig, shape: L90Shape) {
  const posts = [];
  const railHeightIn = config.usage === 'com' ? 42 : 36;
  const cpSku = selectCornerPostSku(railHeightIn);
  const xOff = config.widthIn / 2;
  const arm1HeightIn = config.heightIn * (shape.arm1RunIn / (shape.arm1RunIn + shape.arm2RunIn));
  const turnYIn = config.heightIn - arm1HeightIn;
  const TURN_LANDING_SIZE_IN = shape.landingDepthIn ?? DEFAULT_TURN_LANDING_SIZE_IN;

  // Head platform south corners
  posts.push({
    id: 'cp-head-sw',
    position: {
      x: -xOff * SCALE_IN_TO_UNIT,
      y: config.heightIn * SCALE_IN_TO_UNIT,
      z: EDGE_OVERLAP_IN * SCALE_IN_TO_UNIT,
    },
    heightIn: railHeightIn,
    partNumber: cpSku,
  });
  posts.push({
    id: 'cp-head-se',
    position: {
      x: xOff * SCALE_IN_TO_UNIT,
      y: config.heightIn * SCALE_IN_TO_UNIT,
      z: EDGE_OVERLAP_IN * SCALE_IN_TO_UNIT,
    },
    heightIn: railHeightIn,
    partNumber: cpSku,
  });

  // ADJ-21: Turn landing 4 corners — repositioned to the TRUE landing perimeter (±halfLanding).
  // Pre-ADJ-21 these were at ±xOff (arm-width edge), which left a 12" gap visually for 36"-wide
  // ramps on 60" landings. Now they match the actual landing footprint corners.
  const landingNearZ = shape.arm1RunIn;
  const landingFarZ = shape.arm1RunIn + TURN_LANDING_SIZE_IN;
  const halfLanding = TURN_LANDING_SIZE_IN / 2;
  for (const z of [landingNearZ, landingFarZ]) {
    for (const x of [-halfLanding, halfLanding]) {
      posts.push({
        id: `cp-landing-${z}-${x}`,
        position: {
          x: x * SCALE_IN_TO_UNIT,
          y: turnYIn * SCALE_IN_TO_UNIT,
          z: z * SCALE_IN_TO_UNIT,
        },
        heightIn: railHeightIn,
        partNumber: cpSku,
      });
    }
  }

  // Inside corner post at junction of arm 1 and arm 2
  // For left turn (turnSign=+1): arm 2 extends in +X direction.
  // The inside corner is where arm 1's inside rail meets arm 2's inside rail —
  // at x=+xOff (arm1 right rail), z=arm1RunIn (start of landing / end of arm 1).
  // For right turn: mirror in X (x=-xOff).
  const turnSign = shape.turnDirection === 'left' ? 1 : -1;
  const insideX = turnSign * xOff;
  posts.push({
    id: 'cp-inside-corner',
    position: {
      x: insideX * SCALE_IN_TO_UNIT,
      y: turnYIn * SCALE_IN_TO_UNIT,
      z: landingNearZ * SCALE_IN_TO_UNIT,
    },
    heightIn: railHeightIn,
    partNumber: cpSku,
  });

  // ADJ-21: SE inside pivot post — where SE inside bridge ends and arm 2 INSIDE rail begins.
  // Post-fix: arm 2 INSIDE rail now emerges PILE on the landing north edge (z=landingFarZ).
  // Pre-ADJ-21 it was at z=landingFarZ - xOff (= 186 for palier 60), which was the straddle bug source.
  posts.push({
    id: 'cp-se-inside-pivot',
    position: {
      x: insideX * SCALE_IN_TO_UNIT,
      y: turnYIn * SCALE_IN_TO_UNIT,
      z: landingFarZ * SCALE_IN_TO_UNIT,
    },
    heightIn: railHeightIn,
    partNumber: cpSku,
  });

  // Corner posts at bottom of arm 2 (ground level, where arm 2 rails end)
  // Arm 2 runs along ±X from arm2OriginZ, so its rails are offset in Z by ±xOff.
  // ADJ-21: arm2OriginZ now includes +xOff shift to align arm 2 south face with landing north edge.
  const arm2OriginZ = shape.arm1RunIn + TURN_LANDING_SIZE_IN + xOff;
  const arm2EndX = turnSign * shape.arm2RunIn;
  const arm2ZLeft = arm2OriginZ + xOff;
  const arm2ZRight = arm2OriginZ - xOff;
  posts.push({
    id: 'cp-arm2-sw',
    position: {
      x: arm2EndX * SCALE_IN_TO_UNIT,
      y: 0,
      z: arm2ZLeft * SCALE_IN_TO_UNIT,
    },
    heightIn: railHeightIn,
    partNumber: cpSku,
  });
  posts.push({
    id: 'cp-arm2-se',
    position: {
      x: arm2EndX * SCALE_IN_TO_UNIT,
      y: 0,
      z: arm2ZRight * SCALE_IN_TO_UNIT,
    },
    heightIn: railHeightIn,
    partNumber: cpSku,
  });

  return posts;
}

function buildL90Transition(config: RampConfig, shape: L90Shape) {
  const turnSign = shape.turnDirection === 'left' ? 1 : -1;
  const TURN_LANDING_SIZE_IN = shape.landingDepthIn ?? DEFAULT_TURN_LANDING_SIZE_IN;
  // Transition at end of arm 2 (ground)
  // ADJ-21: arm2OriginZ shifted by +xOff (= widthIn/2) so arm 2 emerges from landing's north edge.
  const arm2OriginZ = shape.arm1RunIn + TURN_LANDING_SIZE_IN + config.widthIn / 2;
  return [
    {
      id: 'trans-arm2-base',
      position: {
        x: turnSign * (shape.arm2RunIn + 5) * SCALE_IN_TO_UNIT,
        y: 0,
        z: arm2OriginZ * SCALE_IN_TO_UNIT,
      },
      rotationY: turnSign * (Math.PI / 2),
      widthIn: config.widthIn,
      partNumber: selectTransitionSku(config.widthIn, config.floor),
    },
  ];
}
